/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import useMarker from 'src/hooks/useMarker';
import useWatchTime from 'src/hooks/useWatchTime';
import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import { IMarkerOptimist } from 'src/interfaces/Marker';
import { MARKER_ACTION, MARKER_STATUS } from 'src/interfaces/enums';
import useUI from 'src/hooks/useUI';
import { onAllChangesSaved, onPageHasUnsavedChanges } from 'src/utils/pageLifeCycleUtils';
import Header from '../../components/NotesHeader';
import NotesList from '../../components/NotesList';
import NotesForm from '../../components/NotesForm';
import { TIME_TO_WAIT_CONTAINER_FLEX_TRANSITION } from '../../components/NotesForm/constants';
import styles from './styles.module.scss';
import { LIST_VISIBILITY, MAX_NUMBER_OF_ATTEMPTS_TO_SAVE_NOTE } from './constants';

export interface INotesContainerProps {
  visible?: boolean;
  onHide: () => void;
}

const getCanAttemptSave = (numberOfAttempts = 0) => {
  return numberOfAttempts < MAX_NUMBER_OF_ATTEMPTS_TO_SAVE_NOTE;
};

const NotesContainer = ({ visible = false, onHide }: INotesContainerProps) => {
  const {
    markers: notes = [],
    deleteMarker: deleteNote,
    saveNewMarker: saveNewNote,
    saveUpdatedMarker: saveUpdatedNote,
    resetStatusAndActionAndNumberOfAttempts,
  } = useMarker();
  const { setPlayerTime, currentTime } = useWatchTime();
  const contentScrollRef = useRef<HTMLDivElement | null>(null);
  const [noteToEdit, setNoteToEdit] = useState<IMarkerOptimist>({} as IMarkerOptimist);
  const [listIsOpenAtValue, setListIsOpenAtValue] = useState(LIST_VISIBILITY.MAX);
  const { fullscreen: isFullscreen } = useUI();

  const { lang } = useTranslation();

  useEffect(() => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    if (!visible) {
      return;
    }

    if (notes.length === 0) {
      setNoteToEdit({} as IMarkerOptimist);
      setListIsOpenAtValue(LIST_VISIBILITY.MIN);
    }
  }, [notes, visible]);

  useEffect(() => {
    if (notes?.some((item) => item.status === MARKER_STATUS.PENDING || item.status === MARKER_STATUS.ERROR)) {
      onPageHasUnsavedChanges();
      return;
    }

    onAllChangesSaved();

    return () => {
      onAllChangesSaved();
    };
  }, [notes]);

  const notesListOpened = useMemo(() => {
    return listIsOpenAtValue === LIST_VISIBILITY.MAX;
  }, [listIsOpenAtValue]);

  const invertFlexGrowFromValue = useCallback((flexGrow = 0, valueToSubtract = LIST_VISIBILITY.MAX) => {
    setListIsOpenAtValue(valueToSubtract - flexGrow);
  }, []);

  const handleOpenNoteForm = useCallback(
    (note: IMarkerOptimist) => {
      if (listIsOpenAtValue === LIST_VISIBILITY.MAX) {
        setListIsOpenAtValue(LIST_VISIBILITY.MIN);
      }
      setNoteToEdit(note);
    },
    [listIsOpenAtValue],
  );

  const handleSubmitNote = useCallback(
    (note: IMarkerOptimist) => {
      const canAttemptSave = getCanAttemptSave(note.numberOfAttempts);
      if (!canAttemptSave) {
        handleOpenNoteForm({ ...note, numberOfAttempts: 0 });
        return;
      }

      note.numberOfAttempts = (note?.numberOfAttempts ?? 0) + 1;

      setListIsOpenAtValue(LIST_VISIBILITY.MAX);

      if (note?.action === MARKER_ACTION.EDIT) {
        saveUpdatedNote(note);
        return;
      }

      saveNewNote(note);
    },
    [saveNewNote, saveUpdatedNote, handleOpenNoteForm],
  );

  const handleDelete = useCallback(
    ({ id, numberOfAttempts }: Pick<IMarkerOptimist, 'id' | 'numberOfAttempts'>) => {
      if (!id) {
        return;
      }

      const canAttemptSave = getCanAttemptSave(numberOfAttempts);

      if (!canAttemptSave) {
        resetStatusAndActionAndNumberOfAttempts(id);
        return;
      }

      deleteNote({
        id,
        numberOfAttempts: (numberOfAttempts ?? 0) + 1,
      });
    },
    [deleteNote, resetStatusAndActionAndNumberOfAttempts],
  );

  const handleRetry = useCallback(
    (note: IMarkerOptimist) => {
      if (note.action === MARKER_ACTION.DELETE) {
        handleDelete(note);
        return;
      }
      handleSubmitNote(note);
    },
    [handleDelete, handleSubmitNote],
  );

  return (
    <div
      className={classNames(styles.container, { [styles.open]: visible, [styles.fullscreen]: isFullscreen })}
      data-testid='notes-container'
    >
      <Header onHide={onHide} />
      <NotesList
        ref={contentScrollRef}
        style={{
          flex: listIsOpenAtValue,
        }}
        className={classNames(styles.containerScroll, { [styles.open]: notesListOpened })}
        notes={notes}
        locale={lang}
        millisecondsToOpeningAnimation={TIME_TO_WAIT_CONTAINER_FLEX_TRANSITION}
        onDelete={handleDelete}
        onEdit={(note) => {
          note.action = MARKER_ACTION.EDIT;
          handleOpenNoteForm(note);
        }}
        onTimeSelect={(time) => {
          setPlayerTime(time);
        }}
        onRetry={handleRetry}
      />
      <NotesForm
        note={noteToEdit}
        visible={!notesListOpened}
        onAddNote={() => {
          handleOpenNoteForm({
            action: MARKER_ACTION.CREATE,
            time: currentTime,
            numberOfAttempts: 0,
          } as IMarkerOptimist);
          setListIsOpenAtValue(LIST_VISIBILITY.MIN);
        }}
        onCancel={() => {
          setListIsOpenAtValue(LIST_VISIBILITY.MAX);
        }}
        onSubmit={handleSubmitNote}
        onResizeForm={invertFlexGrowFromValue}
      />
    </div>
  );
};

export default NotesContainer;
